import React, { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSync } from "@fortawesome/free-solid-svg-icons"
import { COLORS } from "../../utilities/Constants"
import SelectV2 from "../basics/input/SelectV2"
import InputV2 from "../basics/input/InputV2"
import FilteredSelectV2 from "../basics/input/FilteredSelectV2"
import ChoixBulles from "../basics/input/ChoixBulles"
import { getBasePointInteret } from "../../utilities/defaultObjects"
import {
	fetchAdressesV2,
	fetchLocalites,
} from "../../store/actions/adresseActions"
import { URL } from "../../store/actions/depannageActions"
import {
	POINT_INTERET_TYPE_PARC,
	POINT_INTERET_TYPE_FOURRIERE,
	POINT_INTERET_TYPE_ADRESSE,
	POINT_INTERET_TYPE_COORDONNEES,
	POINT_INTERET_TYPE_POLICE,
	POINT_INTERET_TYPE_ATELIER,
} from "./PointInteretComponent"

// Pseudo-type front : « Garage » = entités type 6 — la sélection produit un
// POI type ADRESSE (label + adresse de l'entité), cf. commentaire du modèle
// PointInteret (pas de type POI dédié pour éviter les doublons).
const TYPE_GARAGE_UI = "garage"

// Pastilles par défaut du sélecteur (mission de dépannage).
export const POINTS_INTERETS_V2 = [
	[POINT_INTERET_TYPE_ADRESSE, "Adresse"],
	[POINT_INTERET_TYPE_PARC, "Parc"],
	[POINT_INTERET_TYPE_POLICE, "Parc police"],
	[POINT_INTERET_TYPE_FOURRIERE, "Fourrière"],
	[TYPE_GARAGE_UI, "Garage"],
	// Affichage seulement : fonctionnellement, ça reste le type Coordonnées.
	// Libellé court : les 7 pastilles tiennent sur une ligne en demi-carte
	// (carte « Trajet » de la fiche dépannage).
	[POINT_INTERET_TYPE_COORDONNEES, "Lien Maps"],
]

// GE-69 : + « Atelier » (véhicule à réparer → fiche atelier au départ du
// lieu). Seulement là où ça a un effet : destination finale d'une MISSION
// privée / assistance (dispatch, livraison, proposition d'assistance) —
// pas en police, ni sur le lieu, ni sur la fiche dépannage convertie.
export const POINTS_INTERETS_V2_ATELIER = [
	...POINTS_INTERETS_V2.slice(0, 3),
	[POINT_INTERET_TYPE_ATELIER, "Atelier"],
	...POINTS_INTERETS_V2.slice(3),
]

// Version V2 (tokens, InputV2/SelectV2/FilteredSelectV2) de PointInteretSelect :
// pastilles de type puis, selon le type, select du point d'intérêt existant
// (parc/fourrière), select des garages (entités), saisie d'adresse avec
// autosuggest serveur (rue → adresse complète, NPA → localité+pays) ou
// coordonnées GPS. Même contrat de props que le legacy (`pointInteret`,
// `updatePointInteret(key, value, full)`).
const PointInteretSelectV2 = ({
	pointInteret,
	updatePointInteret,
	list = POINTS_INTERETS_V2,
	inputStyle = null,
	nextRef = null,
	// Pastille « Inconnue » optionnelle (destination de mission) : le POI
	// reste null côté sauvegarde, la saisie se fera sur mobile. `onInconnu`
	// absent = pastille masquée (les autres formulaires ne changent pas).
	inconnu = false,
	onInconnu = null,
	// Texte affiché sous la pastille active — la fiche (déjà convertie) passe
	// le sien, le défaut colle au flux mission → mobile.
	inconnuHint = "La destination sera saisie par le dépanneur sur mobile, avant de quitter le lieu d'intervention.",
	// Compteur : chaque incrément met le focus sur le NPA (FilteredSelectV2
	// rouvre alors sa liste) — « Utiliser cette adresse » quand le NPA a
	// plusieurs localités. Absent = aucun effet (autres usages inchangés).
	signalNpa = 0,
}) => {
	const dispatch = useDispatch()
	const { parcs, fourrieres, polices, ateliers } = useSelector(
		(state) => state.pointInteretInfo,
	)
	const { adressesv2, localites } = useSelector((state) => state.adressesInfo)
	const garages = useSelector((state) => state.autreInfo.garages)
	const [selectedType, setSelectedType] = useState(pointInteret.type)
	// Garage choisi (id) — état d'affichage seulement, le POI produit est de
	// type ADRESSE.
	const [garageId, setGarageId] = useState(0)

	// Le POI peut être remplacé sous le composant sans démontage (feuilletage
	// de fiche, rechargement après sauvegarde) : la pastille active suit alors
	// le type reçu, sinon le formulaire affiché n'est plus celui du POI édité
	// et la première saisie écrase le point de la nouvelle fiche.
	// Exception : le pseudo-type « Garage » produit un POI de type ADRESSE —
	// on ne rétrograde donc pas la pastille quand la prop passe à ADRESSE.
	const typeRecuRef = useRef(pointInteret.type)
	useEffect(() => {
		if (pointInteret.type === typeRecuRef.current) return
		typeRecuRef.current = pointInteret.type
		if (
			selectedType === TYPE_GARAGE_UI &&
			pointInteret.type === POINT_INTERET_TYPE_ADRESSE
		)
			return
		setSelectedType(pointInteret.type)
		setGarageId(0)
	}, [pointInteret.type, selectedType])

	const changeMenu = (newType) => {
		if (inconnu && onInconnu) onInconnu(false)
		updatePointInteret(
			"",
			getBasePointInteret(
				newType === TYPE_GARAGE_UI
					? POINT_INTERET_TYPE_ADRESSE
					: newType,
			),
			true,
		)
		setSelectedType(newType)
		setGarageId(0)
	}

	// Sélection d'un garage : POI type ADRESSE pré-rempli avec le label et la
	// première adresse de l'entité (complétable dans la grille en dessous).
	const selectGarage = (e) => {
		const id = parseInt(e.target.value)
		setGarageId(id)
		const garage = (garages || []).find((g) => g.id === id)
		const base = getBasePointInteret(POINT_INTERET_TYPE_ADRESSE)
		if (!garage) {
			updatePointInteret("", base, true)
			return
		}
		const ad = (garage.adresses || [])[0] || {}
		updatePointInteret(
			"",
			{
				...base,
				label: garage.label,
				adresse: {
					...base.adresse,
					id: ad.id || null,
					adresse_id: ad.adresse_id || null,
					rue: ad.rue || "",
					numero: ad.numero || "",
					code_postal: ad.code_postal || "",
					localite: ad.localite || "",
					pays: ad.pays || "",
					extra_data: ad.extra_data || {},
				},
			},
			true,
		)
	}

	const updateAdresse = (key, value, full = false) => {
		if (full) {
			updatePointInteret("adresse", value)
		} else {
			updatePointInteret("adresse", {
				...pointInteret.adresse,
				[key]: value,
			})
		}
	}

	// Sélection d'une adresse existante (suggestion rue) → remplit tout.
	const selectAdresseComplete = (ad) => {
		if (!ad) return
		updateAdresse(
			"",
			{
				...pointInteret.adresse,
				rue: ad.rue,
				code_postal: ad.code_postal,
				localite: ad.localite,
				pays: ad.pays,
				adresse_id: ad.id,
			},
			true,
		)
	}

	// Sélection d'un NPA → localité + pays.
	const selectLocalite = (l) => {
		if (!l) return
		updateAdresse(
			"",
			{
				...pointInteret.adresse,
				code_postal: String(l.zip),
				localite: l.nom,
				pays: l.pays,
			},
			true,
		)
	}

	const selectPoi = (pois, type) => (e) => {
		const id = parseInt(e.target.value)
		const poi =
			id > 0
				? { ...pois.find((p) => p.id === id) }
				: getBasePointInteret(type)
		updatePointInteret("", poi, true)
	}

	// Refs internes pour le chaînage clavier de l'adresse.
	const numeroRef = React.useRef(null)
	const codePostalRef = React.useRef(null)

	useEffect(() => {
		if (!signalNpa) return undefined
		// Laisse le POI ADRESSE se rendre (la grille d'adresse apparaît).
		const t = setTimeout(() => {
			if (codePostalRef.current) codePostalRef.current.focus()
		}, 120)
		return () => clearTimeout(t)
	}, [signalNpa])
	const localiteRef = React.useRef(null)
	const paysRef = React.useRef(null)

	const adresse = pointInteret.adresse
	// NPA en options string (FilteredSelectV2 filtre en lowercase sur le champ).
	const localiteOptions = (localites || []).map((l) => ({
		...l,
		zip: String(l.zip),
	}))

	// Lien « Vérifier dans Google Maps » dès que le point est exploitable,
	// quel que soit le type : coordonnées → q=lat,lng ; sinon adresse
	// postale ; en dernier recours le label du POI (parc/fourrière sans
	// adresse saisie).
	const mapsUrl = () => {
		if (pointInteret.type === POINT_INTERET_TYPE_COORDONNEES) {
			const extra = (adresse || {}).extra_data || {}
			return extra.latitude && extra.longitude
				? `https://www.google.com/maps?q=${extra.latitude},${extra.longitude}`
				: null
		}
		const a = adresse || {}
		const parts = [
			[a.rue, a.numero].filter(Boolean).join(" "),
			[a.code_postal, a.localite].filter(Boolean).join(" "),
			a.pays,
		].filter(Boolean)
		if (a.rue && a.localite)
			return `https://www.google.com/maps?q=${encodeURIComponent(parts.join(", "))}`
		if (pointInteret.id !== null && pointInteret.label)
			return `https://www.google.com/maps?q=${encodeURIComponent(pointInteret.label)}`
		return null
	}
	const url = mapsUrl()

	return (
		<div>
			<div style={st.pills}>
				{/* Lot « bulles » (15.10) : types de point en niveau 1 ;
				    « Inconnue » reste à part (un état, pas un lieu). */}
				<ChoixBulles
					label="Type de point"
					niveau={1}
					options={list.map((t) => [t[0], t[1]])}
					valeur={inconnu ? null : selectedType}
					onChange={changeMenu}
					// Les bulles coulent avec « Inconnue » dans la même rangée.
					style={{ display: "contents" }}
				/>
				{onInconnu ? (
					<button
						type="button"
						onClick={() => onInconnu(true)}
						style={{
							...st.pill,
							borderStyle: inconnu ? "solid" : "dashed",
							borderColor: inconnu
								? COLORS.warning
								: COLORS.borderStrong,
							backgroundColor: inconnu
								? COLORS.warningLight
								: COLORS.surface,
							color: inconnu ? COLORS.warning : COLORS.textMuted,
						}}
					>
						Inconnue
					</button>
				) : null}
			</div>

			{inconnu ? <div style={st.inconnuHint}>{inconnuHint}</div> : null}

			{!inconnu && selectedType === POINT_INTERET_TYPE_PARC && (
				<SelectV2
					value={pointInteret.id || 0}
					onChange={selectPoi(parcs, POINT_INTERET_TYPE_PARC)}
					nextRef={nextRef}
					style={{ ...st.input, ...inputStyle }}
				>
					<option value={0}>-</option>
					{parcs.map((p) => (
						<option key={`parc_${p.id}`} value={p.id}>
							{p.label}
						</option>
					))}
				</SelectV2>
			)}

			{!inconnu && selectedType === POINT_INTERET_TYPE_POLICE && (
				<SelectV2
					value={pointInteret.id || 0}
					onChange={selectPoi(
						polices || [],
						POINT_INTERET_TYPE_POLICE,
					)}
					nextRef={nextRef}
					style={{ ...st.input, ...inputStyle }}
				>
					<option value={0}>-</option>
					{(polices || []).map((p) => (
						<option key={`police_${p.id}`} value={p.id}>
							{p.label}
						</option>
					))}
				</SelectV2>
			)}

			{!inconnu && selectedType === POINT_INTERET_TYPE_ATELIER && (
				<SelectV2
					value={pointInteret.id || 0}
					onChange={selectPoi(
						ateliers || [],
						POINT_INTERET_TYPE_ATELIER,
					)}
					nextRef={nextRef}
					style={{ ...st.input, ...inputStyle }}
				>
					<option value={0}>-</option>
					{(ateliers || []).map((p) => (
						<option key={`atelier_${p.id}`} value={p.id}>
							{p.label}
						</option>
					))}
				</SelectV2>
			)}

			{!inconnu && selectedType === POINT_INTERET_TYPE_FOURRIERE && (
				<SelectV2
					value={pointInteret.id || 0}
					onChange={selectPoi(
						fourrieres,
						POINT_INTERET_TYPE_FOURRIERE,
					)}
					nextRef={nextRef}
					style={{ ...st.input, ...inputStyle }}
				>
					<option value={0}>-</option>
					{fourrieres.map((f) => (
						<option key={`fourriere_${f.id}`} value={f.id}>
							{f.label}
						</option>
					))}
				</SelectV2>
			)}

			{/* Pas de marge basse : le lien Maps en fin de formulaire porte
			    l'espacement (identique pour tous les types). */}
			{!inconnu && selectedType === TYPE_GARAGE_UI && (
				<div>
					<SelectV2
						value={garageId}
						onChange={selectGarage}
						nextRef={nextRef}
						style={{ ...st.input, ...inputStyle }}
					>
						<option value={0}>-</option>
						{(garages || []).map((g) => (
							<option key={`garage_${g.id}`} value={g.id}>
								{g.label}
							</option>
						))}
					</SelectV2>
					{/* Adresse du garage en lecture seule (vient de la fiche
					    entité — s'y corrige, pas ici). */}
					{garageId > 0 && (
						<div style={st.adresseReadonly}>
							<div style={{ fontWeight: 600 }}>
								{pointInteret.label}
							</div>
							{adresse.rue || adresse.localite ? (
								<>
									<div>
										{[adresse.rue, adresse.numero]
											.filter(Boolean)
											.join(" ")}
									</div>
									<div>
										{[adresse.code_postal, adresse.localite]
											.filter(Boolean)
											.join(" ")}
										{adresse.pays
											? ` — ${adresse.pays}`
											: ""}
									</div>
								</>
							) : (
								<div style={st.adresseWarning}>
									Adresse manquante sur la fiche entité du
									garage — à compléter dans /clients.
								</div>
							)}
						</div>
					)}
				</div>
			)}

			{!inconnu && selectedType === POINT_INTERET_TYPE_COORDONNEES && (
				<CoordonneesInputs
					pointInteret={pointInteret}
					updatePointInteret={updatePointInteret}
					inputStyle={inputStyle}
					nextRef={nextRef}
				/>
			)}

			{/* 2 lignes : rue + n° / NPA + localité + pays (grille 12 col).
			    Pays réduit (quasi toujours « Suisse ») au profit de Localité. */}
			{!inconnu && selectedType === POINT_INTERET_TYPE_ADRESSE && (
				<div style={st.adresseGrid}>
					<div style={{ gridColumn: "span 9" }}>
						<FilteredSelectV2
							options={adressesv2 || []}
							defaultKey="rue"
							optionKeys={["rue", "code_postal", "localite"]}
							value={adresse.rue}
							minChars={2}
							fetch={(rue) =>
								dispatch(fetchAdressesV2({ search: rue }))
							}
							onChange={(rue) => {
								const next = { ...adresse, rue }
								if (rue === "") next.adresse_id = null
								updateAdresse("", next, true)
							}}
							onSelect={selectAdresseComplete}
							nextRef={numeroRef}
							placeholder="Rue"
							inputStyle={inputStyle}
						/>
					</div>
					<InputV2
						ref={numeroRef}
						type="text"
						value={adresse.numero}
						onChange={(e) =>
							updateAdresse("numero", e.target.value)
						}
						nextRef={codePostalRef}
						placeholder="N°"
						style={{
							...st.input,
							...inputStyle,
							gridColumn: "span 3",
						}}
					/>
					<div style={{ gridColumn: "span 3" }}>
						<FilteredSelectV2
							options={localiteOptions}
							defaultKey="zip"
							optionKeys={["zip", "nom"]}
							value={adresse.code_postal}
							minChars={2}
							fetch={(code) =>
								dispatch(fetchLocalites({ search: code }))
							}
							onChange={(code_postal) =>
								updateAdresse("code_postal", code_postal)
							}
							onSelect={selectLocalite}
							inputRef={codePostalRef}
							nextRef={localiteRef}
							placeholder="NPA"
							inputStyle={inputStyle}
						/>
					</div>
					<InputV2
						ref={localiteRef}
						type="text"
						value={adresse.localite}
						onChange={(e) =>
							updateAdresse("localite", e.target.value)
						}
						nextRef={paysRef}
						placeholder="Localité"
						style={{
							...st.input,
							...inputStyle,
							gridColumn: "span 6",
						}}
					/>
					<InputV2
						ref={paysRef}
						type="text"
						value={adresse.pays}
						onChange={(e) => updateAdresse("pays", e.target.value)}
						nextRef={nextRef}
						placeholder="Pays"
						style={{
							...st.input,
							...inputStyle,
							gridColumn: "span 3",
						}}
					/>
				</div>
			)}

			{/* Toujours en dernier, quel que soit le type : même position et
			    même espacement dans tous les formulaires de point d'intérêt. */}
			{!inconnu && url && (
				<a
					href={url}
					target="_blank"
					rel="noopener noreferrer"
					style={st.mapsLink}
				>
					Vérifier sur Google Maps →
				</a>
			)}
		</div>
	)
}

// Pastille « Lien Maps » (fonctionnellement : POI type Coordonnées,
// latitude/longitude dans adresse.extra_data, label = adresse
// reverse-geocodée sinon "lat, lng"). Un seul champ : le lien de partage
// Google Maps du client (maps.app.goo.gl/… — accepte aussi "lat, lng"
// collé) ; le back suit la redirection et renvoie coordonnées + adresse
// (api/resolve-maps-link). Le résultat s'affiche en vert sous le champ :
// label + coordonnées (ou coordonnées seules sans adresse). Le clic Maps se
// construit partout sur lat/lng, jamais sur l'adresse (cf. depLieux.js).
const CoordonneesInputs = ({
	pointInteret,
	updatePointInteret,
	inputStyle,
	nextRef,
}) => {
	const { token } = useSelector((state) => state.navInfo.login)
	const [lien, setLien] = useState("")
	// null ou { statut: "loading" | "erreur", texte } — le succès n'a pas
	// d'état : la ligne verte dérive du POI lui-même (label + extra_data),
	// donc elle s'affiche aussi à la réouverture d'un POI existant.
	const [resolution, setResolution] = useState(null)
	// Garde anti-course : seule la dernière résolution lancée a la parole.
	const resolutionId = React.useRef(0)
	const extra = (pointInteret.adresse || {}).extra_data || {}
	const lat = extra.latitude || ""
	const lng = extra.longitude || ""
	const coordsTexteAffiche = lat && lng ? `${lat}, ${lng}` : ""
	// Label ≠ "lat, lng" = une adresse a été résolue → "adresse — lat, lng".
	const ligneVerte =
		coordsTexteAffiche &&
		(pointInteret.label && pointInteret.label !== coordsTexteAffiche
			? `${pointInteret.label} — ${coordsTexteAffiche}`
			: coordsTexteAffiche)

	const setCoords = (nLat, nLng, libelle = "") => {
		const label = libelle || (nLat && nLng ? `${nLat}, ${nLng}` : "")
		updatePointInteret(
			"",
			{
				...pointInteret,
				// CharField(50) côté modèle.
				label: label.slice(0, 50),
				adresse: {
					...pointInteret.adresse,
					extra_data: { ...extra, latitude: nLat, longitude: nLng },
				},
			},
			true,
		)
	}

	// Accepte un lien maps (court ou complet) ou directement "lat, lng".
	// Fetch direct : résultat purement local au formulaire, pas de slice
	// redux (l'erreur s'affiche sous le champ, pas en toast).
	const resoudreLien = (e) => {
		const value = e.target.value
		setLien(value)
		setResolution(null)
		const texte = value.trim()
		if (!texte) return
		const coordsTexte = texte.match(
			/^(-?\d{1,3}\.\d+)\s*,\s*(-?\d{1,3}\.\d+)$/,
		)
		if (coordsTexte) {
			setCoords(coordsTexte[1], coordsTexte[2])
			return
		}
		if (!/^https?:\/\//.test(texte)) return
		const id = ++resolutionId.current
		setResolution({ statut: "loading" })
		fetch(`${URL}api/resolve-maps-link?url=${encodeURIComponent(texte)}`, {
			headers: { Authorization: `Bearer ${token}` },
		})
			.then(async (res) => {
				const json = await res.json().catch(() => ({}))
				if (!res.ok)
					throw new Error(json.message || "Lien non reconnu.")
				if (id !== resolutionId.current) return
				setCoords(json.latitude, json.longitude, json.adresse || "")
				setResolution(null)
			})
			.catch((err) => {
				if (id !== resolutionId.current) return
				setResolution({
					statut: "erreur",
					texte: err.message || "Lien non reconnu.",
				})
			})
	}

	return (
		<div>
			<InputV2
				type="text"
				value={lien}
				onChange={resoudreLien}
				nextRef={nextRef}
				placeholder="Coller un lien Google Maps (maps.app.goo.gl/…)"
				style={{ ...st.input, ...inputStyle }}
			/>
			{resolution && resolution.statut === "loading" && (
				<div style={st.resolutionInfo}>
					<FontAwesomeIcon icon={faSync} spin /> Résolution du lien…
				</div>
			)}
			{resolution && resolution.statut === "erreur" && (
				<div style={st.resolutionErreur}>{resolution.texte}</div>
			)}
			{!resolution && ligneVerte && (
				<div style={st.resolutionOk}>✓ {ligneVerte}</div>
			)}
		</div>
	)
}

const st = {
	pills: { display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 },
	inconnuHint: {
		padding: "10px 12px",
		border: `1.5px dashed ${COLORS.borderStrong}`,
		borderRadius: 10,
		fontSize: 13,
		color: COLORS.textMuted,
	},
	// « Inconnue » : même gabarit que les bulles de niveau 1.
	pill: {
		display: "inline-flex",
		alignItems: "center",
		boxSizing: "border-box",
		height: 36,
		padding: "0 14px",
		borderRadius: 10,
		border: "1px solid",
		cursor: "pointer",
		fontSize: 13.5,
		fontWeight: 600,
		fontFamily: "inherit",
	},
	input: {
		width: "100%",
		padding: "7px 11px",
		borderRadius: 8,
		border: `1px solid ${COLORS.border}`,
		fontSize: 13,
		color: COLORS.text,
		fontFamily: "inherit",
		backgroundColor: COLORS.surface,
		boxSizing: "border-box",
	},
	adresseGrid: {
		display: "grid",
		gridTemplateColumns: "repeat(12, 1fr)",
		gap: 10,
	},
	resolutionInfo: {
		fontSize: 12,
		color: COLORS.textMuted,
		marginTop: 8,
	},
	resolutionOk: {
		fontSize: 12,
		fontWeight: 600,
		color: COLORS.success,
		marginTop: 8,
	},
	resolutionErreur: {
		fontSize: 12,
		color: COLORS.danger,
		marginTop: 8,
	},
	adresseReadonly: {
		marginTop: 10,
		padding: "10px 12px",
		borderRadius: 8,
		border: `1px solid ${COLORS.border}`,
		backgroundColor: COLORS.bg,
		fontSize: 13,
		color: COLORS.text,
		lineHeight: 1.5,
	},
	adresseWarning: {
		fontSize: 12,
		color: COLORS.danger,
	},
	mapsLink: {
		display: "inline-block",
		marginTop: 8,
		fontSize: 12,
		fontWeight: 600,
		color: COLORS.accent,
		textDecoration: "none",
	},
}

export default PointInteretSelectV2
